// Page Commandes - Entre Particuliers
// Pour voir ses achats et ventes
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Package, ShoppingBag, Tag, Clock, ChevronRight,
  CheckCircle, Truck, AlertCircle, MessageCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';

export default function CommandesAnnoncesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('achats'); // 'achats' ou 'ventes'
  const token = localStorage.getItem('ecosystem_token');

  // Si non connecté, afficher un message d'incitation
  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />

        <main className="max-w-2xl mx-auto px-4 py-12">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
              <Package size={40} className="text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Mes commandes</h1>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Connecte-toi pour voir tes achats et ventes entre particuliers.
            </p>
            
            <div className="space-y-3">
              <Link to="/connexion" className="block">
                <Button className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] rounded-xl font-semibold">
                  Se connecter
                </Button>
              </Link>
              <Link to="/inscription" className="block">
                <Button variant="outline" className="w-full h-12 border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF]/5 rounded-xl font-semibold">
                  Créer un compte gratuit
                </Button>
              </Link>
            </div>

            <p className="text-xs text-gray-400 mt-6">
              Tes transactions sont sécurisées et tes données protégées.
            </p>
          </motion.div>
        </main>
      </div>
    );
  }

  // Mock data pour les commandes (à remplacer par API)
  const mockAchats = [];
  const mockVentes = [];

  const currentItems = activeTab === 'achats' ? mockAchats : mockVentes;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Mes commandes</h1>
              <p className="text-sm text-gray-500">Achats et ventes entre particuliers</p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('achats')}
              className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'achats'
                  ? 'border-[#1E40AF] text-[#1E40AF]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ShoppingBag size={18} className="inline mr-2" />
              Mes achats
            </button>
            <button
              onClick={() => setActiveTab('ventes')}
              className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'ventes'
                  ? 'border-[#F97316] text-[#F97316]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Tag size={18} className="inline mr-2" />
              Mes ventes
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {currentItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-12 text-center"
          >
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              activeTab === 'achats' ? 'bg-blue-50' : 'bg-orange-50'
            }`}>
              {activeTab === 'achats' ? (
                <ShoppingBag size={40} className="text-blue-400" />
              ) : (
                <Tag size={40} className="text-orange-400" />
              )}
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">
              {activeTab === 'achats' ? 'Aucun achat' : 'Aucune vente'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              {activeTab === 'achats' 
                ? "Tu n'as pas encore effectué d'achat. Parcours les annonces pour trouver ce qu'il te faut."
                : "Vous n'avez pas encore vendu d'article. Déposez une annonce pour commencer à vendre."
              }
            </p>
            <Link to={activeTab === 'achats' ? '/annonces' : '/annonces/deposer'}>
              <Button className={`rounded-xl font-semibold ${
                activeTab === 'achats' 
                  ? 'bg-[#1E40AF] hover:bg-[#1E3A8A]' 
                  : 'bg-[#F97316] hover:bg-[#EA580C]'
              }`}>
                {activeTab === 'achats' ? 'Parcourir les annonces' : 'Déposer une annonce'}
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {/* Liste des commandes */}
          </div>
        )}
      </main>
    </div>
  );
}
